import { parseCheckoutPricingInput } from "./checkout-input-schemas.ts";
import { resolveCheckoutSelection } from "./checkout-selection.ts";
import { deepFreeze } from "./deep-freeze.ts";
import type {
  LockerverseCatalog,
  LockerverseCheckoutEstimate,
  LockerverseEstimateCheckoutInput,
} from "./types.ts";

export function estimateLockerverseCheckout(
  catalog: LockerverseCatalog,
  input: LockerverseEstimateCheckoutInput
): LockerverseCheckoutEstimate {
  const { selection, ...pricingInput } = input;
  let pricing: ReturnType<typeof parseCheckoutPricingInput>;
  try {
    pricing = parseCheckoutPricingInput(pricingInput);
  } catch (error) {
    throw new TypeError("Checkout pricing is invalid.", { cause: error });
  }
  const includeServiceFee =
    pricing.includeServiceFee ?? catalog.widget.fields.serviceFee;
  const { tipCents = 0 } = pricing;
  const { items, lineItems, paymentOption } = resolveCheckoutSelection(
    catalog,
    selection
  );
  const subtotalCents = lineItems.reduce(
    (total, lineItem) => total + lineItem.subtotalCents,
    0
  );
  if (
    !Number.isSafeInteger(subtotalCents) ||
    subtotalCents <= 0 ||
    subtotalCents > 2_147_483_647
  ) {
    throw new TypeError("Checkout total is invalid.");
  }
  const discountableSubtotalCents = lineItems.reduce(
    (total, lineItem) =>
      total + (lineItem.acceptsDiscounts ? lineItem.subtotalCents : 0),
    0
  );
  const serviceFeeCents = includeServiceFee
    ? Math.round(
        (subtotalCents * catalog.pricingPolicy.serviceFeeRateBasisPoints) /
          10_000
      )
    : 0;
  const amountCents = subtotalCents + serviceFeeCents;
  const totalCents = amountCents + tipCents;
  if (!Number.isSafeInteger(totalCents) || totalCents > 2_147_483_647) {
    throw new TypeError("Checkout total is invalid.");
  }

  return deepFreeze({
    amountCents,
    appliedDiscount: null,
    authoritative: false,
    discountableSubtotalCents,
    discountCents: 0,
    items,
    lineItems,
    paymentOption,
    serviceFeeCents,
    subtotalCents,
    tipCents,
    totalCents,
  });
}
